/**
 * OfferMirror — PaywallPage「限时优惠 / Offer」tab（ADM-PAY-S005 运营支付活动 offer 管理）。
 *
 * 一个 tab 内完整 CRUD：卡片头（说明 + 新建）+ KPI strip + 过滤 + .tbl 表格 + 行操作（编辑 + 更多菜单：
 * 启停/复制 offer_id/删）+ 创建/编辑弹窗 + 激活/停用/删除二次确认。镜像同页 ProductMirror，但 offer 字段
 * （scenario / 窗口 / 资格 / 启停）、无金额无多语言。**RBAC 关键差异**：offer 写**全 operator+ 不收窄**
 * （删/停也 operator+）——offer 停/删危害=失效入口死链（后端 reverse 护栏拦 + C 端优雅降级），无
 * product_metadata 停用/软删的 paid-but-nothing 面。故所有写控件统一 gate canWrite（operator+），不引
 * root/admin 收窄。删/停的强约束由后端 reverse 护栏 PAY_OFFER_REFERENCED 权威拦截；建 / 激活不校验渠道落点
 * （A8 §6.2 / §6.4）。前端按设计稿加删除预护栏（先查 references）+ 删除 requireTyping DELETE 防误删。
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { ELIGIBILITY_LABELS, OFFER_SCENARIO_ITEMS } from '~/data/adapters/payment-offer-adapter';
import { type OfferRow, usePaymentOffers } from '~/data/queries';
import { paymentOffersApi } from '~/hooks/use-payment-offers';
import { fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { ContextMenu } from '~/ui/ContextMenu';
import { ConfirmDialog } from '~/ui/Dialog';
import { FilterButton } from '~/ui/FilterButton';
import { SearchInput } from '~/ui/SearchInput';
import { Select } from '~/ui/Select';
import { SectionSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { PaymentOfferEditor } from './dialogs/PaymentOfferEditor';
import { PaymentOfferProductOrderDialog } from './dialogs/PaymentOfferProductOrderDialog';

interface ShowApp {
  id: string;
  name: string;
  platform?: string;
}

/** offer 写动作角色集（root/admin/operator）；与 server payment-offers route requireRole 一致——offer 全写不收窄 */
function useCanWriteOffer(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

/**
 * 派生活动徽标（纯展示，权威判定在后端 serving）：由 is_active 与「当前时间 vs [开始, 结束)」推导。
 * 语义与设计稿 deriveOfferBadge 一致——**停用活动直接返「已停用」灰点**（不再按时间窗显示「进行中」，
 * 避免停用活动仍标进行中的误导）；上架活动再按时间窗分 未开始 / 进行中 / 已结束。
 */
function deriveOfferBadge(o: OfferRow): { label: string; cls: string } {
  if (!o.isActive) return { label: '已停用', cls: '' };
  const now = Date.now();
  const s = new Date(o.startsAt).getTime();
  const e = new Date(o.endsAt).getTime();
  if (now < s) return { label: '未开始', cls: 'chip-info' };
  if (now >= e) return { label: '已结束', cls: '' };
  return { label: '进行中', cls: 'chip-pos' };
}

/** scenario → 中文 label（文案与展示序对齐设计稿 OFFER_SCENARIOS：builtin 内建场景排前）*/
function scenarioLabel(v: string): string {
  return OFFER_SCENARIO_ITEMS.find((s) => s.value === v)?.label ?? v;
}

/** references → 引用方文案（DTO 只有 kind/id、无 label——接真：以 Banner#id / 推送#id 定位引用方）*/
function refsText(o: OfferRow): string {
  return o.references
    .map((r) => `${r.kind === 'banner_slide' ? 'Banner' : '推送'}#${r.id}`)
    .join('、');
}

/** offer 写错误文案：PAY_OFFER_REFERENCED 时把后端 details.references（kind/id）拼进提示，运营据此定位下架引用方 */
function offerErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof AdminApiError && err.code === 'PAY_OFFER_REFERENCED') {
    const refs = (err.details?.references as Array<{ kind: string; id: string }> | undefined) ?? [];
    const list = refs
      .map((r) => `${r.kind === 'banner_slide' ? 'Banner' : '推送'}#${r.id}`)
      .join('、');
    return list ? `${err.message}（引用方：${list}）` : err.message;
  }
  return err instanceof Error ? err.message : fallback;
}

export function OfferMirror({ app }: { app: ShowApp }) {
  const { data: rows, isLoading, total, hasMore } = usePaymentOffers(app.id);
  const queryClient = useQueryClient();
  const canWrite = useCanWriteOffer();
  const [q, setQ] = useState('');
  const [scenarios, setScenarios] = useState<string[]>([]);
  // 状态过滤三档（设计稿）：按 is_active 上架/停用过滤，非时间窗
  const [listed, setListed] = useState<'all' | 'on' | 'off'>('all');
  const [editor, setEditor] = useState<{ item?: OfferRow } | null>(null);
  const [deleting, setDeleting] = useState<OfferRow | null>(null);
  const [deactivating, setDeactivating] = useState<OfferRow | null>(null);
  const [activating, setActivating] = useState<OfferRow | null>(null);
  const [productOrderOffer, setProductOrderOffer] = useState<OfferRow | null>(null);

  // app.id==='all' 仅当无任何真实 App（showApp 回退占位）；此时无具体 App 维度 → 禁新建
  const noApp = app.id === 'all';

  const list = rows.filter((o) => {
    if (q) {
      const hay = `${scenarioLabel(o.scenario)} ${o.scenario} ${o.id}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    if (scenarios.length && !scenarios.includes(o.scenario)) return false;
    if (listed === 'on' && !o.isActive) return false;
    if (listed === 'off' && o.isActive) return false;
    return true;
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => paymentOffersApi.remove(id),
    onSuccess: async () => {
      toast('已删除运营支付活动', { tone: 'warn', icon: 'IconTrash' });
      await queryClient.invalidateQueries({ queryKey: ['payment-offers', app.id] });
      setDeleting(null);
    },
    onError: (err) => {
      toast(offerErrorMessage(err, '删除失败'), { tone: 'neg', icon: 'IconAlert' });
    },
  });

  // 上架/下架（启停）：offer 全 operator+（不收窄）。下架/删被后端 reverse 护栏权威拦截（仍被引用则 PAY_OFFER_REFERENCED）
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? paymentOffersApi.activate(id) : paymentOffersApi.deactivate(id),
    onSuccess: async (_data, { active }) => {
      toast(active ? '已上架 · 活动入口对 C 端可用' : '已下架 · 活动入口对 C 端不可用', {
        tone: active ? 'pos' : 'warn',
        icon: active ? 'IconCheck' : 'IconBan',
      });
      setDeactivating(null);
      setActivating(null);
      await queryClient.invalidateQueries({ queryKey: ['payment-offers', app.id] });
    },
    onError: (err) => {
      toast(offerErrorMessage(err, '操作失败'), { tone: 'neg', icon: 'IconAlert' });
    },
  });

  // 激活预检已随 dev-docs #530 退役：契约允许「offer 先建/先激活、渠道后配」，服务端不再校验落点存在性。
  const tryActivate = (o: OfferRow) => {
    setActivating(o);
  };

  // 删除预护栏（设计稿 tryDelete）：仍被引用 → toast 拦截，不弹确认（后端 PAY_OFFER_REFERENCED 仍权威）
  const tryDelete = (o: OfferRow) => {
    if (o.references.length > 0) {
      toast(
        `无法删除 · 该 offer 被 ${o.references.length} 处引用（${refsText(o)}）· 请先清理引用`,
        {
          tone: 'warn',
          icon: 'IconBan',
        },
      );
      return;
    }
    setDeleting(o);
  };

  // KPI 三档与行内徽标同口径（deriveOfferBadge）：已停用活动不计入进行中/未开始/已结束，避免与徽标自相矛盾
  const kpis: { label: string; v: string | number; tone?: 'pos' | 'info' }[] = [
    { label: '全部活动', v: total },
    {
      label: '进行中',
      v: rows.filter((o) => deriveOfferBadge(o).label === '进行中').length,
      tone: 'pos',
    },
    {
      label: '未开始',
      v: rows.filter((o) => deriveOfferBadge(o).label === '未开始').length,
      tone: 'info',
    },
    { label: '已结束', v: rows.filter((o) => deriveOfferBadge(o).label === '已结束').length },
  ];

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div
        className="card"
        style={{ padding: 14, background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 10, alignItems: 'flex-start' }}>
          <Icons.IconInfo size={14} style={{ color: 'var(--info)', marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.55 }}>
            <span style={{ fontWeight: 500 }}>{app.name} · 运营支付活动 offer</span>
            <span style={{ color: 'var(--fg-muted)', marginLeft: 6 }}>
              限时优惠 / 充值活动 · 复用既有 <span className="mono">scenario</span> 通道配置 +
              有效期窗口 + 资格 · 活动生成稳定 <span className="mono">offer_id</span> 供 Banner /
              推送携带
            </span>
          </div>
          <button
            className="btn btn-sm"
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['payment-offers', app.id] });
              toast('正在重新拉取运营支付活动', { tone: 'info', icon: 'IconRefresh' });
            }}
          >
            <Icons.IconRefresh size={11} />
            刷新
          </button>
          <button
            className="btn btn-accent btn-sm"
            disabled={!canWrite || noApp}
            title={
              noApp
                ? '请先在顶栏选择具体 App'
                : canWrite
                  ? undefined
                  : '权限不足（仅 root/admin/operator 可创建）'
            }
            onClick={() => setEditor({})}
          >
            <Icons.IconPlus size={11} />
            创建活动
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {kpis.map((s) => (
          <div key={s.label} className="card" style={{ padding: 14 }}>
            <div className="stat-label">{s.label}</div>
            <div
              className="stat-value"
              style={{
                fontSize: 22,
                marginTop: 4,
                color:
                  s.tone === 'pos'
                    ? 'var(--pos)'
                    : s.tone === 'info'
                      ? 'var(--info)'
                      : 'var(--fg-strong)',
              }}
            >
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="hstack" style={{ gap: 8 }}>
        <SearchInput
          width={280}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜索 offer_id / 通道场景"
        />
        <FilterButton
          label="通道场景"
          value={scenarios}
          onChange={setScenarios}
          options={OFFER_SCENARIO_ITEMS.map((s) => ({ value: s.value, label: s.label }))}
          width={150}
        />
        <Select
          value={listed}
          onChange={(e) => setListed(e.target.value as 'all' | 'on' | 'off')}
          style={{ width: 130 }}
        >
          <option value="all">全部状态</option>
          <option value="on">仅上架</option>
          <option value="off">仅停用</option>
        </Select>
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          共 {list.length} / {rows.length}
          {hasMore ? ` · 仅显示前 ${rows.length}（共 ${total}）` : ''}
        </span>
      </div>

      {isLoading && rows.length === 0 ? (
        <SectionSkeleton />
      ) : rows.length === 0 ? (
        // 无活动空态卡（设计稿 1:1）：icon + 文案 + CTA 创建按钮；noApp 时提示先选 App、CTA 禁用
        <div className="card" style={{ padding: '48px 0', textAlign: 'center' }}>
          <Icons.IconFlame size={24} style={{ color: 'var(--fg-faint)' }} />
          <div style={{ fontSize: 13, color: 'var(--fg-muted)', marginTop: 10 }}>
            {noApp ? '请先在顶栏选择具体 App' : `${app.name} 下暂无运营支付活动`}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--fg-faint)', marginTop: 4 }}>
            创建活动后将按通道场景在有效期窗口内向资格用户下发
          </div>
          <button
            className="btn btn-sm btn-accent"
            style={{ marginTop: 14 }}
            disabled={!canWrite || noApp}
            title={
              noApp
                ? '请先在顶栏选择具体 App'
                : canWrite
                  ? undefined
                  : '权限不足（仅 root/admin/operator 可创建）'
            }
            onClick={() => setEditor({})}
          >
            <Icons.IconPlus size={11} />
            创建活动
          </button>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <Table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 230 }}>offer_id</th>
                <th>通道场景</th>
                <th>有效期窗口</th>
                <th>资格</th>
                <th>状态</th>
                <th>创建时间</th>
                <th style={{ width: 132 }} />
              </tr>
            </thead>
            <tbody>
              {list.map((o) => {
                const badge = deriveOfferBadge(o);
                return (
                  <tr key={o.id}>
                    {/* offer_id 独立首列（设计稿 1:1）：截断 mono（title 全 id）+ 复制按钮 + 「被引用」计数 chip */}
                    <td>
                      <div className="hstack" style={{ gap: 4 }}>
                        <span
                          className="mono"
                          style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}
                          title={o.id}
                        >
                          {o.id.slice(0, 8)}…{o.id.slice(-4)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon"
                          style={{ width: 20, height: 20 }}
                          title="复制 offer_id"
                          onClick={() => {
                            navigator.clipboard?.writeText(o.id);
                            toast(`已复制 offer_id ${o.id}`, { tone: 'pos', icon: 'IconCopy' });
                          }}
                        >
                          <Icons.IconCopy size={10} />
                        </button>
                        {o.references.length > 0 && (
                          <span
                            className="chip"
                            style={{ fontSize: 9, padding: '0 5px', flexShrink: 0 }}
                            title={`被引用：${refsText(o)}`}
                          >
                            <Icons.IconExternal size={8} />
                            {o.references.length}
                          </span>
                        )}
                      </div>
                    </td>
                    {/* 通道场景列：统一 chip-info 蓝 + scenario raw 副行（运营据此对照通道场景配置）*/}
                    <td>
                      <span className="chip chip-info" style={{ fontSize: 10 }}>
                        {scenarioLabel(o.scenario)}
                      </span>
                      <div
                        className="mono"
                        style={{ fontSize: 9.5, color: 'var(--fg-faint)', marginTop: 2 }}
                      >
                        {o.scenario}
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                      {fmtDateTime(o.startsAt)} ~ {fmtDateTime(o.endsAt)}
                    </td>
                    <td>
                      {/* 资格 new_user 用 chip-accent 强调（对齐设计稿 OfferList）；其余普通 chip */}
                      <span
                        className={`chip ${o.eligibility === 'new_user' ? 'chip-accent' : ''}`}
                        style={{ fontSize: 10 }}
                      >
                        {ELIGIBILITY_LABELS[o.eligibility]}
                      </span>
                    </td>
                    {/* 状态：启停 chip（上架/停用）+ 时间派生徽标 chip（● 进行中/未开始/已结束/已停用）并排，设计稿 1:1 */}
                    <td>
                      <div className="hstack" style={{ gap: 4 }}>
                        <span
                          className={`chip ${o.isActive ? 'chip-pos' : 'chip-warn'}`}
                          style={{ fontSize: 10 }}
                        >
                          {o.isActive ? '上架' : '停用'}
                        </span>
                        <span className={`chip ${badge.cls}`} style={{ fontSize: 10 }}>
                          ● {badge.label}
                        </span>
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                      {fmtDateTime(o.createdAt)}
                    </td>
                    {/* 行操作（设计稿 1:1）：「编辑」文字按钮 + ContextMenu 更多菜单（启停/复制 offer_id/删除）*/}
                    <td>
                      <div className="hstack" style={{ gap: 4, justifyContent: 'flex-end' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          disabled={!canWrite}
                          title={
                            canWrite
                              ? '编辑（仅改有效期窗口 + 资格 · 通道 / offer_id 不可改）'
                              : '权限不足'
                          }
                          onClick={() => canWrite && setEditor({ item: o })}
                        >
                          <Icons.IconEdit size={11} />
                          编辑
                        </button>
                        <ContextMenu
                          items={[
                            {
                              label: '上架商品与顺序',
                              icon: 'IconList',
                              onClick: () => setProductOrderOffer(o),
                            },
                            o.isActive
                              ? {
                                  label: '停用活动',
                                  icon: 'IconPause',
                                  disabled: !canWrite || toggleActiveMutation.isPending,
                                  title: canWrite ? undefined : '权限不足',
                                  onClick: () => setDeactivating(o),
                                }
                              : {
                                  label: '激活活动',
                                  icon: 'IconPlay',
                                  disabled: !canWrite || toggleActiveMutation.isPending,
                                  title: canWrite ? undefined : '权限不足',
                                  onClick: () => tryActivate(o),
                                },
                            {
                              label: '复制 offer_id',
                              icon: 'IconCopy',
                              onClick: () => {
                                navigator.clipboard?.writeText(o.id);
                                toast(`已复制 offer_id ${o.id}`, { tone: 'pos', icon: 'IconCopy' });
                              },
                            },
                            '---',
                            {
                              label: '删除活动',
                              icon: 'IconTrash',
                              tone: 'danger',
                              disabled: !canWrite,
                              title: canWrite ? undefined : '权限不足',
                              onClick: () => tryDelete(o),
                            },
                          ]}
                        >
                          <button
                            className="btn btn-ghost btn-icon"
                            style={{ width: 24, height: 24 }}
                            title="更多操作"
                          >
                            <Icons.IconMoreV size={12} />
                          </button>
                        </ContextMenu>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          {list.length === 0 && (
            <div
              style={{
                padding: '32px 0',
                textAlign: 'center',
                fontSize: 12,
                color: 'var(--fg-muted)',
              }}
            >
              没有符合筛选条件的活动
            </div>
          )}
        </div>
      )}

      <div className="card-flat" style={{ padding: '8px 12px', background: 'var(--bg-subtle)' }}>
        <div className="hstack" style={{ gap: 8, fontSize: 11.5, color: 'var(--fg-muted)' }}>
          <Icons.IconInfo size={11} style={{ color: 'var(--info)' }} />
          <span>
            ADM-PAY-S005 · scenario 创建后不可改（改 scenario = 换活动）·
            创建/激活不校验渠道落点（未配时 serving 落空）· 停用/删除前若仍被 active Banner /
            未投递推送引用将被拒（先下架引用方）
          </span>
        </div>
      </div>

      {productOrderOffer && (
        <PaymentOfferProductOrderDialog
          offerId={productOrderOffer.id}
          appId={app.id}
          platform={app.platform ?? 'IOS'}
          onClose={() => setProductOrderOffer(null)}
        />
      )}
      {editor && (
        <PaymentOfferEditor appId={app.id} item={editor.item} onClose={() => setEditor(null)} />
      )}
      {deleting && (
        <ConfirmDialog
          title={`删除 offer「${scenarioLabel(deleting.scenario)}」？`}
          message={
            <>
              软删除 · 活动从列表移除，已携带该 offer_id 的历史 Banner / 推送链接将失效。
              {/* 设计稿 details 行：ConfirmDialog 无 details prop，以 message 内 mono 行承载 */}
              <div className="mono" style={{ fontSize: 11, marginTop: 6 }}>
                offer_id · {deleting.id}
              </div>
            </>
          }
          tone="danger"
          icon="IconTrash"
          requireTyping="DELETE"
          confirmLabel="永久删除"
          onConfirm={() => {
            if (!deleteMutation.isPending) deleteMutation.mutate(deleting.id);
          }}
          onClose={() => setDeleting(null)}
        />
      )}
      {deactivating && (
        <ConfirmDialog
          title={`下架「${scenarioLabel(deactivating.scenario)}」运营支付活动？`}
          message="停用后活动入口对 C 端失效。若仍被 active Banner / 未投递推送引用，后端将拒绝并提示先下架引用方。"
          tone="warn"
          icon="IconArrowDown"
          confirmLabel="下架（停用活动）"
          onConfirm={() => {
            if (!toggleActiveMutation.isPending)
              toggleActiveMutation.mutate({ id: deactivating.id, active: false });
          }}
          onClose={() => setDeactivating(null)}
        />
      )}
      {activating && (
        <ConfirmDialog
          title={`激活 offer「${scenarioLabel(activating.scenario)}」？`}
          message={
            <>
              激活后在有效期窗口内向资格用户下发。
              <div className="mono" style={{ fontSize: 11, marginTop: 6 }}>
                offer_id · {activating.id}
              </div>
            </>
          }
          icon="IconPlay"
          confirmLabel="确认激活"
          onConfirm={() => {
            if (!toggleActiveMutation.isPending)
              toggleActiveMutation.mutate({ id: activating.id, active: true });
          }}
          onClose={() => setActivating(null)}
        />
      )}
    </div>
  );
}
